import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { HelmetProvider } from "react-helmet-async";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { ExamProvider } from "@/contexts/ExamContext";
import { ImpersonationProvider } from "@/contexts/ImpersonationContext";
import * as Sentry from "@sentry/react";
import { useEffect } from "react";
import { analytics } from "@/lib/analytics";
import Index from "./pages/Index";
import NotFound from "./pages/NotFound";
import Dashboard from "./pages/Dashboard";
import Practice from "./pages/Practice";
import Flashcards from "./pages/Flashcards";
import Notes from "./pages/Notes";
import Mock from "./pages/Mock";
import Study from "./pages/Study";
import Admin from "./pages/Admin";
import { Navigate } from "react-router-dom";
import { AdminOverview } from "@/components/dashboard/AdminOverview";
import TopicManager from "@/components/admin/TopicManager";
import NoteManager from "@/components/admin/NoteManager";
import MCQManager from "@/components/admin/MCQManager";
import BulkLabelManager from "@/components/admin/BulkLabelManager";
import MCQImport from "@/components/admin/MCQImport";
import AIManager from "@/components/admin/AIManager";
import { MCQNotesLinkManager } from "@/components/admin/MCQNotesLinkManager";
import UserManager from "@/components/admin/UserManager";
import ExamLabelManager from "@/components/admin/ExamLabelManager";
import AuditLogViewer from "@/components/admin/AuditLogViewer";
import { UserFeedbackManager } from "@/components/admin/UserFeedbackManager";
import { FeedbackAnalyticsDashboard } from "@/components/admin/FeedbackAnalyticsDashboard";
import MCQReviewPlatform from "@/components/admin/MCQReviewPlatform";
import AdminBilling from "@/components/admin/AdminBilling";
import AdminSettingsPage from "@/components/admin/AdminSettings";
import Analytics from "./pages/Analytics";
import Pricing from "./pages/Pricing";
import Auth from "./pages/Auth";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import QuizResults from "./pages/QuizResults";
import SubscriptionSuccess from "./pages/SubscriptionSuccess";
import SubscriptionCancelled from "./pages/SubscriptionCancelled";
import TopicAnalytics from "./pages/TopicAnalytics";
import StudyPlanSetup from "./pages/StudyPlanSetup";
import StudyPlanDashboard from "./pages/StudyPlanDashboard";
import Privacy from "./pages/Privacy";
import Terms from "./pages/Terms";
import Contact from "./pages/Contact";
import Settings from "./pages/Settings";
import Bookmarks from "./pages/Bookmarks";
import QuizBuilder from "./pages/QuizBuilder";
import { AppSidebar } from "./components/layout/AppSidebar";
import { SiteHeader } from "./components/layout/SiteHeader";
import { Footer } from "./components/layout/Footer";
import { MobileBottomNav } from "./components/navigation/MobileBottomNav";
import { CommandPalette } from "./components/navigation/CommandPalette";
import { ExamDeepLinkHandler } from "./components/exam/ExamDeepLinkHandler";

import { OfflineSync } from "./components/offline/OfflineSync";

const queryClient = new QueryClient();

// Component to track page views
const AnalyticsTracker = () => {
  const location = useLocation();
  
  useEffect(() => {
    analytics.trackPageview();
  }, [location]);
  
  return null;
};

const SentryRoutes = Sentry.withSentryRouting(Routes);

// Layout wrapper that conditionally shows sidebar for authenticated users
const AppLayout = ({ children }: { children: React.ReactNode }) => {
  const { user } = useAuth();
  const location = useLocation();
  
  // Public routes that don't need sidebar
  const publicRoutes = ["/", "/auth", "/forgot-password", "/reset-password", "/pricing", "/privacy", "/terms", "/contact"];
  const isPublicRoute = publicRoutes.includes(location.pathname);
  
  // Admin routes have their own layout
  const isAdminRoute = location.pathname.startsWith('/admin');

  if (!user || isPublicRoute || isAdminRoute) {
    return (
      <div className="flex flex-col min-h-screen">
        {!isAdminRoute && <SiteHeader />}
        <main id="main-content" className="flex-1 container py-6" tabIndex={-1}>
          {children}
        </main>
        {!isAdminRoute && <Footer />}
      </div>
    );
  }

  return (
    <SidebarProvider defaultOpen>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <SidebarInset className="flex-1">
          <header className="sticky top-0 z-10 flex h-14 items-center gap-4 border-b bg-background px-6">
            <SidebarTrigger />
            <div className="flex-1" />
          </header>
          <main id="main-content" className="flex-1 p-6" tabIndex={-1}>
            {children}
          </main>
        </SidebarInset>
      </div>
      <MobileBottomNav />
      <CommandPalette />
      
      <OfflineSync />
    </SidebarProvider>
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <HelmetProvider>
        <AuthProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AnalyticsTracker />
            <ExamProvider>
            <ImpersonationProvider>
            <ExamDeepLinkHandler />
            <AppLayout>
              <Sentry.ErrorBoundary fallback={<ErrorFallback />}>
                <SentryRoutes>
                  <Route path="/" element={<Index />} />
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
                  <Route path="/practice" element={<ProtectedRoute requiresSubscription={true}><Practice /></ProtectedRoute>} />
                  <Route path="/flashcards" element={<ProtectedRoute requiresSubscription={true}><Flashcards /></ProtectedRoute>} />
                  <Route path="/notes" element={<ProtectedRoute requiresSubscription={true}><Notes /></ProtectedRoute>} />
                  <Route path="/study" element={<ProtectedRoute requiresSubscription={true}><Study /></ProtectedRoute>} />
                  <Route path="/study/:topicId" element={<ProtectedRoute requiresSubscription={true}><Study /></ProtectedRoute>} />
                  <Route path="/mock" element={<ProtectedRoute requiresSubscription={true}><Mock /></ProtectedRoute>} />
                  <Route path="/admin" element={<ProtectedRoute><Admin /></ProtectedRoute>}>
                    <Route index element={<Navigate to="/admin/overview" replace />} />
                    <Route path="overview" element={<AdminOverview />} />
                    <Route path="topics" element={<TopicManager />} />
                    <Route path="notes" element={<NoteManager />} />
                    <Route path="mcqs" element={<MCQManager />} />
                    <Route path="labels" element={<BulkLabelManager />} />
                    <Route path="import" element={<MCQImport />} />
                    <Route path="ai" element={<AIManager />} />
                    <Route path="linking" element={<MCQNotesLinkManager />} />
                    <Route path="users" element={<UserManager />} />
                    <Route path="billing" element={<AdminBilling />} />
                    <Route path="exam-labels" element={<ExamLabelManager />} />
                    <Route path="audit" element={<AuditLogViewer />} />
                    <Route path="feedback" element={<UserFeedbackManager />} />
                    <Route path="analytics" element={<FeedbackAnalyticsDashboard />} />
                    <Route path="settings" element={<AdminSettingsPage />} />
                    <Route path="review" element={<MCQReviewPlatform />} />
                  </Route>
                  <Route path="/analytics" element={<ProtectedRoute requiresSubscription={true}><Analytics /></ProtectedRoute>} />
                  <Route path="/analytics/topic/:topicId" element={<ProtectedRoute requiresSubscription={true}><TopicAnalytics /></ProtectedRoute>} />
                  <Route path="/study-plan/setup" element={<ProtectedRoute><StudyPlanSetup /></ProtectedRoute>} />
                  <Route path="/study-plan" element={<ProtectedRoute><StudyPlanDashboard /></ProtectedRoute>} />
                  <Route path="/quiz-results" element={<ProtectedRoute><QuizResults /></ProtectedRoute>} />
                  <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
                  <Route path="/bookmarks" element={<ProtectedRoute><Bookmarks /></ProtectedRoute>} />
                  <Route path="/quiz-builder" element={<ProtectedRoute requiresSubscription={true}><QuizBuilder /></ProtectedRoute>} />
                  <Route path="/pricing" element={<Pricing />} />
                  <Route path="/subscription-success" element={<SubscriptionSuccess />} />
                  <Route path="/subscription-cancelled" element={<SubscriptionCancelled />} />
                  <Route path="/privacy" element={<Privacy />} />
                  <Route path="/terms" element={<Terms />} />
                  <Route path="/contact" element={<Contact />} />
                  {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                  <Route path="*" element={<NotFound />} />
                </SentryRoutes>
              </Sentry.ErrorBoundary>
            </AppLayout>
            </ImpersonationProvider>
            </ExamProvider>
          </BrowserRouter>
        </AuthProvider>
      </HelmetProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

// Error fallback component
const ErrorFallback = () => (
  <div className="flex flex-col items-center justify-center min-h-[400px] gap-4">
    <h1 className="text-2xl font-bold text-destructive">Something went wrong</h1>
    <p className="text-muted-foreground">We've been notified and are working on a fix.</p>
    <button
      onClick={() => window.location.reload()}
      className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90"
    >
      Reload Page
    </button>
  </div>
);

export default App;
